import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { arrayOf, array, func, object, string, number } from 'prop-types';
import { compose } from 'redux';
import classNames from 'classnames';

import { injectIntl, intlShape, FormattedMessage } from '../../../../../util/reactIntl';
import { propTypes } from '../../../../../util/types';
import { convertPrice, formatMoney } from '../../../../../util/currency';
import { withCloudinaryJpgFormat } from '../../../../../util/cloudinary';
import {
  ensureListing,
  isDeliveryOrShippingAvailable,
  isPickupRequired,
  getPerDayPriceRange,
  resolveRatingAndReviews,
} from '../../../../../util/data';

import css from './MapPreview.module.css';
import IconCard from '../../../../../components/SavedCardDetails/IconCard/IconCard';

const formatDistance = (distance) => {
  if (!distance || distance?.kilometers === undefined || distance?.miles === undefined) return null;
  return {
    km: distance.kilometers.toFixed(1),
    miles: distance.miles.toFixed(1),
  };
};

const SmallPin = () => (
  <svg width="12" height="13" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 0C3.79 0 2 1.79 2 4c0 3 4 8.5 4 8.5S10 7 10 4c0-2.21-1.79-4-4-4zm0 5.5A1.5 1.5 0 1 1 6 2.5a1.5 1.5 0 0 1 0 3z" fill="#A4A7AE" />
  </svg>
);

const SmallTruck = () => (
  <svg width="14" height="12" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0H1C.45 0 0 .45 0 1v9c0 .55.45 1 1 1h1.17A2.5 2.5 0 0 0 4.5 13a2.5 2.5 0 0 0 2.33-2h5.34A2.5 2.5 0 0 0 14.5 13a2.5 2.5 0 0 0 2.33-2H18c.55 0 1-.45 1-1V6L15 1h-3V0zm0 1.5h2.5L17 5.5V10h-.67A2.5 2.5 0 0 0 14.5 8.5a2.5 2.5 0 0 0-2.33 1.5H6.83A2.5 2.5 0 0 0 4.5 8.5 2.5 2.5 0 0 0 2.17 10H1V1h11v.5zM4.5 10.5c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm10 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z" fill="#A4A7AE" />
  </svg>
);

const VerifiedBadge = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="8" fill="#101828" />
    <path d="M4.5 8L7 10.5L11.5 5.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Preview Listing Card component
const PreviewListingCard = props => {
  const {
    className,
    clickHandler,
    intl,
    listing,
    urlToListing,
    config,
    selectedCurrency,
    exchangeRates,
    currentUserCountryCode,
  } = props;

  const { title, price, publicData } = listing.attributes;
  const listingImages = publicData?.listingImages || [];
  const firstImage = listingImages.length > 0 ? listingImages[0] : null;

  const { prices, access_to_rentals, additionalFees, city } = publicData || {};

  const pickupRequired = isPickupRequired(access_to_rentals);
  const deliveryOrShipping = isDeliveryOrShippingAvailable(access_to_rentals, additionalFees);

  const distanceInfo = listing?.attributes?.distance
    ? formatDistance(listing.attributes.distance)
    : null;

  const defaultReviewCount = config?.listing?.defaultReviewCount ?? 0;
  const defaultRating = config?.listing?.defaultRating ?? 0;
  const authorPublicData = listing?.author?.attributes?.profile?.publicData || {};
  const { rating, reviews } = resolveRatingAndReviews(authorPublicData, defaultRating, defaultReviewCount);
  const storeDisplayName = listing?.author?.attributes?.profile?.displayName || null;

  const hasExchangeRates = exchangeRates && exchangeRates.length > 0;

  const perDayRange = hasExchangeRates
    ? getPerDayPriceRange(prices, exchangeRates, selectedCurrency)
    : null;

  const convertedPrice = hasExchangeRates && price
    ? convertPrice(exchangeRates, price.amount, price.currency, selectedCurrency)
    : null;

  const priceDisplay = perDayRange ? (
    perDayRange.isSingleTier ? (
      <FormattedMessage
        id="ListCard.perDaySingle"
        values={{
          price: formatMoney(intl, perDayRange.minPrice),
          perDay: intl.formatMessage({ id: 'ListCard.perDayLabel' }),
        }}
      />
    ) : (
      <FormattedMessage
        id="ListCard.perDayRange"
        values={{
          min: formatMoney(intl, perDayRange.minPrice),
          max: formatMoney(intl, perDayRange.maxPrice),
          perDay: intl.formatMessage({ id: 'ListCard.perDayLabel' }),
        }}
      />
    )
  ) : convertedPrice ? (
    <span>{formatMoney(intl, convertedPrice)}</span>
  ) : null;

  const firstImageMaybe = firstImage?.secure_url ? (
    <img className={css.previewImage} src={withCloudinaryJpgFormat(firstImage.secure_url)} alt={title} loading="lazy" />
  ) : (
    <div className={css.noImageWrapper}>
      <svg color="#333" width="100px" height="100px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M7.828 5l-1-1H22v15.172l-1-1v-.69l-3.116-3.117-.395.296-.714-.714.854-.64a.503.503 0 0 1 .657.046L21 16.067V5zM3 20v-.519l2.947-2.947a1.506 1.506 0 0 0 .677.163 1.403 1.403 0 0 0 .997-.415l2.916-2.916-.706-.707-2.916 2.916a.474.474 0 0 1-.678-.048.503.503 0 0 0-.704.007L3 18.067V5.828l-1-1V21h16.172l-1-1zM17 8.5A1.5 1.5 0 1 1 15.5 7 1.5 1.5 0 0 1 17 8.5zm-1 0a.5.5 0 1 0-.5.5.5.5 0 0 0 .5-.5zm5.646 13.854l.707-.707-20-20-.707.707z" />
        <path fill="none" d="M0 0h24v24H0z" />
      </svg>
    </div>
  );

  const handleCardClick = (e) => {
    e.preventDefault();
    if (clickHandler) {
      clickHandler(listing);
    }
  };

  return (
    <a
      alt={title}
      className={classNames(css.previewAnchor, className)}
      href={urlToListing}
      onClick={handleCardClick}
    >
      <div className={css.previewCard}>
        {/* Row 1: Image + (distance · title · chips) */}
        <div className={css.imageSectionWrapper}>
          <div className={css.imageSection}>
            {firstImageMaybe}
          </div>

          <div className={css.infoSection}>
            {distanceInfo && (
              <div className={css.distanceBadge}>
                <IconCard brand="location" />
                <FormattedMessage
                  id="ListCard.distance"
                  values={{
                    distance: currentUserCountryCode === 'US' ? distanceInfo.miles : distanceInfo.km,
                    unit: currentUserCountryCode === 'US' ? 'miles' : 'km',
                  }}
                />
              </div>
            )}

            <div className={css.previewTitle}>{title}</div>

            {(city || pickupRequired || deliveryOrShipping) && (
              <div className={css.accessInfoStack}>
                {(city || pickupRequired) && (
                  <div className={css.accessInfoLine}>
                    <SmallPin />
                    <span>
                      {city ? (
                        <FormattedMessage id="ListCard.pickupInCity" values={{ city }} />
                      ) : (
                        <FormattedMessage id="ListCard.pickupAvailable" />
                      )}
                    </span>
                  </div>
                )}
                {deliveryOrShipping && (
                  <div className={css.accessInfoLine}>
                    <SmallTruck />
                    <span><FormattedMessage id="ListCard.deliveryAvailable" /></span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Price range + Rent Now */}
        {priceDisplay && (
          <div className={css.priceActionRow}>
            <div className={css.priceRange}>{priceDisplay}</div>
            <div className={css.rentNowBtn}>
              <FormattedMessage id="ListCard.rentNow" />
            </div>
          </div>
        )}

        {/* Row 3: Verification + rating (centered) */}
        <div className={css.bottomRow}>
          <span className={css.verifiedIcon}>
            <VerifiedBadge size={15} />
          </span>
          <IconCard brand="star" />
          <span className={css.ratingText}>{(rating || 0).toFixed(1)}</span>
          <span className={css.reviewCount}>
            <FormattedMessage id="ListCard.reviews" values={{ reviews }} />
          </span>
        </div>
        {storeDisplayName && (
          <div className={css.storeNameRow}>By {storeDisplayName}</div>
        )}
      </div>
    </a>
  );
};

PreviewListingCard.defaultProps = {
  className: null,
  clickHandler: null,
  currentUserCountryCode: null,
};

PreviewListingCard.propTypes = {
  className: string,
  clickHandler: func,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  urlToListing: string.isRequired,
  config: object.isRequired,
  selectedCurrency: string.isRequired,
  exchangeRates: array.isRequired,
  currentUserCountryCode: string,
};

// Main MapPreview component
const MapPreviewComponent = props => {
  const {
    className,
    rootClassName,
    intl,
    listings,
    activeListingId,
    createURLToListing,
    onListingPreviewClicked,
    config,
    selectedCurrency = 'USD',
    exchangeRates = [],
    currentUserCountryCode = null,
    panelOpenProgress = 0,
    panelPointerHandlers = null,
  } = props;

  const fadeProgress = Math.min(1, Math.max(0, panelOpenProgress));
  let previewInlineStyles = {
    opacity: 1 - fadeProgress * 7,
    transform: `translateY(${fadeProgress * 20}px)`,
    pointerEvents: fadeProgress >= 0.95 ? 'none' : 'auto',
  };

  if (!listings || listings.length === 0) {
    return null;
  }

  let listingsToShow = [];

  if (Array.isArray(listings)) {
    listingsToShow = listings;
  } else if (activeListingId && listings.length > 0) {
    const activeListing = listings.find(l => l.id.uuid === activeListingId.uuid);
    if (activeListing) {
      listingsToShow = [activeListing];
    } else {
      listingsToShow = [listings[0]];
    }
  } else if (listings.length > 0) {
    listingsToShow = [listings[0]];
  }

  if (listingsToShow.length === 0) {
    return null;
  }

  const hasMultipleListings = listingsToShow.length > 1;
  const classes = classNames(rootClassName || css.root, className, hasMultipleListings && css.rootWithMultipleListing);

  const cardProps = {
    clickHandler: onListingPreviewClicked,
    intl,
    config,
    selectedCurrency,
    exchangeRates,
    currentUserCountryCode,
  };

  const dragStartRef = useRef({ x: 0, y: 0 });
  const isHorizontalScrollingRef = useRef(false);
  const previewContainerRef = useRef(null);

  const [touchAction, setTouchAction] = useState('none');
  const [containerTouchAction, setContainerTouchAction] = useState(
    hasMultipleListings ? 'pan-x' : 'none'
  );

  const HORIZONTAL_SCROLL_DETECTION_THRESHOLD = 8;
  const HORIZONTAL_VERTICAL_RATIO = 1.2;

  useEffect(() => {
    if (hasMultipleListings) {
      setContainerTouchAction('pan-x');
    } else {
      setContainerTouchAction('none');
    }
  }, [hasMultipleListings]);

  useEffect(() => {
    if (!panelPointerHandlers) {
      setTouchAction('auto');
    } else if (touchAction !== 'pan-x') {
      setTouchAction('none');
    }
  }, [panelPointerHandlers, touchAction]);

  const handleRootPointerMove = useCallback(
    event => {
      if (!panelPointerHandlers) return;
    },
    [panelPointerHandlers]
  );

  const handleRootPointerDown = useCallback(event => {
    if (!panelPointerHandlers) return;
    dragStartRef.current = { x: event.clientX, y: event.clientY };
    isHorizontalScrollingRef.current = false;
    setTouchAction('none');
  }, [panelPointerHandlers]);

  const handleRootPointerUp = useCallback(() => {
    if (!panelPointerHandlers) return;
    setTouchAction('none');
    setContainerTouchAction(hasMultipleListings ? 'pan-x' : 'none');
    isHorizontalScrollingRef.current = false;
  }, [panelPointerHandlers, hasMultipleListings]);

  const handleContainerPointerDown = useCallback(event => {
    if (!panelPointerHandlers || !hasMultipleListings) return;
    dragStartRef.current = { x: event.clientX, y: event.clientY };
    isHorizontalScrollingRef.current = false;
    setContainerTouchAction('pan-x');
  }, [panelPointerHandlers, hasMultipleListings]);

  const handleContainerPointerMove = useCallback(event => {
    if (!panelPointerHandlers || !hasMultipleListings) return;
    const deltaX = Math.abs(event.clientX - dragStartRef.current.x);
    const deltaY = Math.abs(event.clientY - dragStartRef.current.y);

    const hasSignificantMovement = deltaX > HORIZONTAL_SCROLL_DETECTION_THRESHOLD ||
      deltaY > HORIZONTAL_SCROLL_DETECTION_THRESHOLD;

    if (!hasSignificantMovement) return;

    if (deltaY > deltaX * HORIZONTAL_VERTICAL_RATIO && deltaY > HORIZONTAL_SCROLL_DETECTION_THRESHOLD) {
      setTouchAction('none');
      setContainerTouchAction('none');
      isHorizontalScrollingRef.current = false;
    } else if (deltaX > deltaY * HORIZONTAL_VERTICAL_RATIO && deltaX > HORIZONTAL_SCROLL_DETECTION_THRESHOLD) {
      event.stopPropagation();
      setTouchAction('pan-x');
      setContainerTouchAction('pan-x');
      isHorizontalScrollingRef.current = true;
    }
  }, [panelPointerHandlers, hasMultipleListings]);

  const enhancedHandlers = useMemo(() => {
    if (!panelPointerHandlers) return {};
    return {
      ...panelPointerHandlers,
      onPointerDown: event => {
        handleRootPointerDown(event);
        panelPointerHandlers.onPointerDown?.(event);
      },
      onPointerMove: event => {
        handleRootPointerMove(event);
        panelPointerHandlers.onPointerMove?.(event);
      },
      onPointerUp: event => {
        handleRootPointerUp(event);
        panelPointerHandlers.onPointerUp?.(event);
      },
      onPointerCancel: event => {
        handleRootPointerUp(event);
        panelPointerHandlers.onPointerCancel?.(event);
      },
    };
  }, [panelPointerHandlers, handleRootPointerDown, handleRootPointerMove, handleRootPointerUp]);

  previewInlineStyles = { ...previewInlineStyles, touchAction };

  return (
    <div className={classes} style={previewInlineStyles} {...enhancedHandlers}>
      <div className={css.mapcloseSwipeHandle}>
        <span></span>
      </div>
      <div
        ref={previewContainerRef}
        className={classNames(css.previewContainer, { [css.scrollable]: hasMultipleListings })}
        onPointerDown={hasMultipleListings ? handleContainerPointerDown : undefined}
        onPointerMove={hasMultipleListings ? handleContainerPointerMove : undefined}
        style={hasMultipleListings ? { touchAction: containerTouchAction } : undefined}
      >
        {listingsToShow.map((listing) => (
          <PreviewListingCard
            key={listing.id.uuid}
            listing={ensureListing(listing)}
            urlToListing={createURLToListing(listing)}
            {...cardProps}
          />
        ))}
      </div>
    </div>
  );
};

MapPreviewComponent.defaultProps = {
  className: null,
  rootClassName: null,
  listings: [],
  activeListingId: null,
  onListingPreviewClicked: null,
  selectedCurrency: 'USD',
  exchangeRates: [],
  currentUserCountryCode: null,
  onClose: null,
  panelOpenProgress: 0,
  panelPointerHandlers: null,
};

MapPreviewComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listings: arrayOf(propTypes.listing),
  activeListingId: propTypes.uuid,
  createURLToListing: func.isRequired,
  onListingPreviewClicked: func,
  config: object.isRequired,
  selectedCurrency: string,
  exchangeRates: array,
  currentUserCountryCode: string,
  onClose: func,
  panelOpenProgress: number,
  panelPointerHandlers: object,
};

export default compose(injectIntl)(MapPreviewComponent);
